import { ChangeDetectionStrategy, Component } from '@angular/core';
import { BuilderComponentType, PaletteCategory, PaletteItem } from '../interfaces/builder-screen.models';
import { BuilderScreenBetaService } from '../services/builder-screen.service';
import { BuilderValidationService } from '../services/builder-validation.service';
import { NzMessageService } from 'ng-zorro-antd/message';

@Component({
  selector: 'app-builder-palette-beta',
  templateUrl: './palette.component.html',
  styleUrls: ['./palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class PaletteBetaComponent {
  private readonly iconMap: Record<string, string> = {
    tabs: 'folder',
    panel: 'layout',
    row: 'column-width',
    text: 'font-size',
    textarea: 'align-left',
    number: 'number',
    switch: 'swap',
    dropdown: 'down-square',
    button: 'play-circle',
    image: 'picture',
    simpletable: 'table',
    table: 'insert-row-below',
    datagrid: 'database',
    spreadsheet: 'file-excel',
    date: 'calendar',
    select: 'select',
    querySelect: 'search',
    keyValue: 'key',
    keyValueObject: 'block',
    tag: 'tags',
    search: 'file-search',
    divider: 'line',
    section: 'group',
  };

  // Sistema de paleta dupla V5 com categorias
  public readonly categories: ReadonlyArray<PaletteCategory> = [
    {
      id: 'basic',
      labelKey: 'builderScreen.palette.basic',
      descriptionKey: 'builderScreen.palette.basic.desc',
      items: [
        { type: 'tabs', labelKey: 'builderScreen.palette.tabs', descriptionKey: 'builderScreen.palette.tabs.desc' },
        { type: 'panel', labelKey: 'builderScreen.palette.panel', descriptionKey: 'builderScreen.palette.panel.desc' },
        { type: 'row', labelKey: 'builderScreen.palette.row', descriptionKey: 'builderScreen.palette.row.desc' },
        { type: 'text', labelKey: 'builderScreen.palette.text', descriptionKey: 'builderScreen.palette.text.desc' },
        { type: 'textarea', labelKey: 'builderScreen.palette.textarea', descriptionKey: 'builderScreen.palette.textarea.desc' },
        { type: 'number', labelKey: 'builderScreen.palette.number', descriptionKey: 'builderScreen.palette.number.desc' },
        { type: 'switch', labelKey: 'builderScreen.palette.switch', descriptionKey: 'builderScreen.palette.switch.desc' },
        { type: 'dropdown', labelKey: 'builderScreen.palette.dropdown', descriptionKey: 'builderScreen.palette.dropdown.desc' },
        { type: 'button', labelKey: 'builderScreen.palette.button', descriptionKey: 'builderScreen.palette.button.desc' },
        { type: 'image', labelKey: 'builderScreen.palette.image', descriptionKey: 'builderScreen.palette.image.desc' },
        { type: 'simpletable', labelKey: 'builderScreen.palette.simpletable', descriptionKey: 'builderScreen.palette.simpletable.desc' },
        { type: 'table', labelKey: 'builderScreen.palette.table', descriptionKey: 'builderScreen.palette.table.desc' }
      ]
    },
    {
      id: 'advanced',
      labelKey: 'builderScreen.palette.advanced',
      descriptionKey: 'builderScreen.palette.advanced.desc',
      items: [
        { type: 'datagrid', labelKey: 'builderScreen.palette.datagrid', descriptionKey: 'builderScreen.palette.datagrid.desc' },
        { type: 'spreadsheet', labelKey: 'builderScreen.palette.spreadsheet', descriptionKey: 'builderScreen.palette.spreadsheet.desc' },
        { type: 'date', labelKey: 'builderScreen.palette.date', descriptionKey: 'builderScreen.palette.date.desc' },
        { type: 'select', labelKey: 'builderScreen.palette.select', descriptionKey: 'builderScreen.palette.select.desc' },
        { type: 'querySelect', labelKey: 'builderScreen.palette.querySelect', descriptionKey: 'builderScreen.palette.querySelect.desc' },
        { type: 'keyValue', labelKey: 'builderScreen.palette.keyValue', descriptionKey: 'builderScreen.palette.keyValue.desc' },
        { type: 'keyValueObject', labelKey: 'builderScreen.palette.keyValueObject', descriptionKey: 'builderScreen.palette.keyValueObject.desc' },
        { type: 'tag', labelKey: 'builderScreen.palette.tag', descriptionKey: 'builderScreen.palette.tag.desc' },
        { type: 'search', labelKey: 'builderScreen.palette.search', descriptionKey: 'builderScreen.palette.search.desc' },
        { type: 'divider', labelKey: 'builderScreen.palette.divider', descriptionKey: 'builderScreen.palette.divider.desc' },
        { type: 'section', labelKey: 'builderScreen.palette.section', descriptionKey: 'builderScreen.palette.section.desc' }
      ]
    }
  ];

  // Compatibilidade com versões anteriores - mantém availableItems para referências existentes
  public readonly availableItems: ReadonlyArray<PaletteItem> = this.categories.reduce((acc, cat) => [...acc, ...cat.items], [] as PaletteItem[]);

  constructor(
    private readonly builderService: BuilderScreenBetaService,
    private readonly validationService: BuilderValidationService,
    private readonly message: NzMessageService
  ) {}

  onAdd(type: BuilderComponentType): void {
    // Validação antes de adicionar componente
    const currentState = this.builderService.currentState;
    const selected = currentState.nodes.find(n => n.id === currentState.selectedId) || null;
    
    // A validação já é feita no builderService.addNode(), mas vamos fazer aqui também
    // para dar um feedback mais rápido no próprio palette
    const validation = this.validationService.validateComponentPlacement(
      type,
      selected,
      currentState.nodes
    );
    
    if (!validation.isValid && type !== 'tabs' && type !== 'panel') {
      // Para componentes normais, mostrar tooltip de erro
      this.message.warning(
        validation.errorMessage || 'Este componente não pode ser adicionado aqui',
        { nzDuration: 4000 }
      );
    }
    
    // Sempre chamar addNode - ele tem a lógica completa de validação e criação automática
    this.builderService.addNode(type);
  }

  getIcon(type: string): string {
    return this.iconMap[type] || 'appstore';
  }

  // Função de rastreamento para nz-tabs
  trackByCategory(index: number, category: PaletteCategory): string {
    return category.id;
  }

  // Função de rastreamento para itens
  trackByItem(index: number, item: PaletteItem): string {
    return item.type;
  }
}


